import {
  Component,
  forwardRef,
  Input,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroupDirective,
  NG_VALUE_ACCESSOR,
  NgForm,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { InputSize, InputState } from '../../../assets/types/dtoTypes';
import { LucideAngularModule } from 'lucide-angular';
import { NgClass, NgIf } from '@angular/common';
import { MatHint } from '@angular/material/input';

@Component({
  selector: 'app-input',
  standalone: true,
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  imports: [
    LucideAngularModule,
    NgClass,
    NgIf,
    MatHint,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements ControlValueAccessor, ErrorStateMatcher {
  @Input() placeholder: string = '';
  @Input() size: InputSize;
  @Input() state: InputState;
  @Input() hint: string = '';
  @Input() icon: string = '';
  @Input() withNoIcon = false;
  @Input() type: string = 'text';

  @Input() showLabel: boolean = false;
  @Input() label: string = '';

  @Input() leftIcon: string = '';
  @Input() rightIcon: string = '';
  @Input() showRightIcon: boolean = false;
  @Input() showLeftIcon: boolean = false;

  @Input() min?: string;
  @Input() max?: string;

  value: string = '';
  isDisabled: boolean = false;

  // expose enums to the template
  protected readonly InputStateEnum = InputState;

  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  writeValue(value: string): void {
    this.value = value ?? '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  onFocus(): void {
    // keep if you still use this state externally
    this.state = InputState.ACTIVE_FOCUSED ?? this.state;
  }

  onBlur(): void {
    this.state = InputState.REGULAR ?? this.state;
    this.onTouched();
  }

  onInput(value: string): void {
    this.value = value;
    this.onChange(value);
  }

  // ErrorStateMatcher implementation
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const invalid = !!control && control.invalid;
    const touched = !!control && (control.touched || form?.submitted);
    return !!(invalid && touched);
  }

  // ---- helpers to map enums -> CSS classes for new design ----

  get sizeClass(): string {
    // adapt these to your real enum values
    switch (this.size) {
      case InputSize.SMALL:
        return 'bpz-input-sm';
      case InputSize.LARGE:
        return 'bpz-input-lg';
      default:
        return 'bpz-input-lg';
    }
  }

  get stateClass(): string {
    switch (this.state) {
      case InputState.ERROR:
        return 'bpz-input-error';
      case InputState.SUCCESS:
        return 'bpz-input-success';
      default:
        return ''; // regular state
    }
  }

  get hasIcon(): boolean {
    return (this.showLeftIcon && !!this.leftIcon) || (this.showRightIcon && !!this.rightIcon);
  }
}
